import { MAIN_PATH, type NodeId, activeWindow } from "./graph";
import { getOrgan, initialValues } from "./organs";
import type { ControlValue, HostMsg, Instruction, OrganDef, PeerMsg } from "./types";

const ADVANCE_THRESHOLD = 2;

export interface HostPlayerInit {
  peerId: string;
  organIds: NodeId[];
  send(msg: HostMsg): void;
}

interface HostPlayerState {
  peerId: string;
  organs: OrganDef[];
  values: Record<string, Record<string, ControlValue>>;
  currentInstruction?: Instruction;
  send(msg: HostMsg): void;
}

export interface HostGameOptions {
  targetCompleted: number;
  durationSec: number;
  bolusName: string;
  onProgress(done: number, target: number, timeLeft: number): void;
  onEnd(done: number, target: number, bolusReachedStool: boolean): void;
}

export class HostGame {
  private players = new Map<string, HostPlayerState>();
  private done = 0;
  private running = false;
  private startedAt = 0;
  private tickHandle: number | null = null;
  private seq = 0;
  private bolusIndex = 0;
  private advanceCounter = 0;

  constructor(private opts: HostGameOptions) {}

  setOptions(partial: Partial<HostGameOptions>) {
    this.opts = { ...this.opts, ...partial };
  }

  addPlayer(init: HostPlayerInit) {
    const organs = init.organIds.map(getOrgan);
    const values: Record<string, Record<string, ControlValue>> = {};
    for (const o of organs) values[o.id] = initialValues(o);
    this.players.set(init.peerId, {
      peerId: init.peerId,
      organs,
      values,
      send: init.send,
    });
  }

  removePlayer(peerId: string) {
    this.players.delete(peerId);
  }

  currentStation(): NodeId {
    return MAIN_PATH[this.bolusIndex];
  }

  /** Test-only: force a specific instruction for a reader (bypasses generator). */
  forceInstruction(
    readerPeerId: string,
    partial: Omit<Instruction, "id" | "readerPeerId" | "issuedAt">,
  ) {
    const reader = this.players.get(readerPeerId);
    if (!reader) return;
    reader.currentInstruction = {
      id: `f${++this.seq}`,
      readerPeerId,
      issuedAt: Date.now(),
      ...partial,
    };
  }

  /** Test-only: move bolus to a specific main-path station. */
  debugSetBolus(station: NodeId) {
    const idx = MAIN_PATH.indexOf(station);
    if (idx < 0) throw new Error(`Not on main path: ${station}`);
    this.bolusIndex = idx;
    this.advanceCounter = 0;
  }

  /** Test-only: run the generator once and return the instruction. */
  generateForTesting(readerPeerId: string): Instruction | undefined {
    const reader = this.players.get(readerPeerId);
    if (!reader) return undefined;
    return this.generateFor(reader);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.done = 0;
    this.bolusIndex = 0;
    this.advanceCounter = 0;
    this.startedAt = Date.now();

    const peerAssign = [...this.players.values()].map((p) => ({
      peerId: p.peerId,
      organs: p.organs.map((o) => o.id),
    }));

    for (const p of this.players.values()) {
      p.send({ type: "assign", peers: peerAssign });
    }

    this.broadcastBolus();
    for (const p of this.players.values()) this.issueInstruction(p);
    this.tickHandle = globalThis.setInterval?.(() => this.tick(), 500) ?? null;
  }

  stop() {
    this.running = false;
    if (this.tickHandle !== null) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
  }

  handlePeerMsg(peerId: string, msg: PeerMsg) {
    if (msg.type !== "control") return;
    const actor = this.players.get(peerId);
    if (!actor) return;
    const store = actor.values[msg.organId];
    if (!store) return;
    store[msg.controlId] = msg.value;

    const satisfied = this.findSatisfied(msg.organId, msg.controlId, msg.value);
    if (!satisfied) return;

    this.done += 1;
    const reader = this.players.get(satisfied.readerPeerId);
    reader?.send({ type: "pulse", ok: true, organId: msg.organId });
    actor.send({ type: "pulse", ok: true, organId: msg.organId });
    if (reader) {
      reader.currentInstruction = undefined;
      this.issueInstruction(reader);
    }

    const currentStation = this.currentStation();
    if (msg.organId === currentStation) {
      this.advanceCounter += 1;
      if (this.advanceCounter >= ADVANCE_THRESHOLD) {
        this.advanceBolus();
      }
    }

    if (
      currentStation === "enddarm" &&
      msg.organId === "enddarm" &&
      msg.controlId === "ausscheiden"
    ) {
      this.end(true);
    }
  }

  private advanceBolus() {
    if (this.bolusIndex >= MAIN_PATH.length - 1) return;
    this.bolusIndex += 1;
    this.advanceCounter = 0;
    this.broadcastBolus();
    // Window changed — readers whose foreign pool was empty may now have
    // options. Re-issue for anyone without a live instruction.
    for (const p of this.players.values()) {
      if (!p.currentInstruction) this.issueInstruction(p);
    }
  }

  private broadcastBolus() {
    const current = this.currentStation();
    const msg: HostMsg = {
      type: "bolus",
      current,
      active: [...activeWindow(current)],
      mainIndex: this.bolusIndex,
      bolusName: this.opts.bolusName,
    };
    for (const p of this.players.values()) p.send(msg);
  }

  private findSatisfied(
    organId: NodeId,
    controlId: string,
    value: ControlValue,
  ): Instruction | undefined {
    for (const p of this.players.values()) {
      const ins = p.currentInstruction;
      if (!ins) continue;
      if (ins.organId !== organId) continue;
      if (ins.controlId !== controlId) continue;
      if (matches(value, ins.expected)) return ins;
    }
    return undefined;
  }

  private issueInstruction(reader: HostPlayerState) {
    const instruction = this.generateFor(reader);
    reader.currentInstruction = instruction;
    reader.send({ type: "instruction", instruction: instruction ?? null });
  }

  private generateFor(reader: HostPlayerState): Instruction | undefined {
    const window = activeWindow(this.currentStation());

    // Spaceteam-Regel: eine Anweisung richtet sich nie an den Reader selbst.
    // Wenn keine fremden Organe im Window sind, bleibt der Reader stumm und
    // reagiert nur auf das, was andere vorlesen.
    const foreign = [...this.players.values()]
      .filter((p) => p.peerId !== reader.peerId)
      .flatMap((p) => p.organs.filter((o) => window.has(o.id)));
    if (foreign.length === 0) return undefined;

    const organ = randomItem(foreign);
    const owner = this.ownerOf(organ.id);
    const currentValues = owner?.values[organ.id] ?? {};
    const control = randomItem(organ.controls);

    let expected: ControlValue;
    let text: string;
    switch (control.kind) {
      case "button":
        expected = true;
        text = renderText(organ, control, this.currentStation(), this.opts.bolusName);
        break;
      case "lever": {
        const current = Boolean(currentValues[control.id]);
        expected = !current;
        text = `${expected ? "Aktiviert" : "Deaktiviert"} ${organ.name}: ${control.label}!`;
        break;
      }
      case "slider":
      case "dial": {
        const min = control.min ?? 0;
        const max = control.max ?? 10;
        const step = control.step ?? 1;
        const current = Number(currentValues[control.id]);
        let v = current;
        let guard = 0;
        while (v === current && guard++ < 10) v = randomStep(min, max, step);
        expected = v;
        const verb = control.kind === "dial" ? "Dreht" : "Stellt";
        text = `${verb} ${organ.name}-${control.label} auf ${v}${control.unit ?? ""}!`;
        break;
      }
    }
    return {
      id: `i${++this.seq}`,
      readerPeerId: reader.peerId,
      organId: organ.id,
      controlId: control.id,
      expected,
      text,
      issuedAt: Date.now(),
    };
  }

  private ownerOf(organId: NodeId): HostPlayerState | undefined {
    for (const p of this.players.values()) {
      if (p.values[organId]) return p;
    }
    return undefined;
  }

  private tick() {
    const elapsed = (Date.now() - this.startedAt) / 1000;
    const timeLeft = Math.max(0, this.opts.durationSec - elapsed);
    const msg: HostMsg = {
      type: "progress",
      done: this.done,
      target: this.opts.targetCompleted,
      timeLeft,
    };
    for (const p of this.players.values()) p.send(msg);
    this.opts.onProgress(this.done, this.opts.targetCompleted, timeLeft);
    if (timeLeft <= 0) this.end(false);
  }

  private end(bolusReachedStool: boolean) {
    if (!this.running) return;
    this.stop();
    const msg: HostMsg = {
      type: "end",
      done: this.done,
      target: this.opts.targetCompleted,
      bolusReachedStool,
    };
    for (const p of this.players.values()) p.send(msg);
    this.opts.onEnd(this.done, this.opts.targetCompleted, bolusReachedStool);
  }
}

function renderText(
  organ: OrganDef,
  control: { label: string; id: string },
  station: NodeId,
  bolusName: string,
): string {
  if (station === "mund" && control.id === "kauen") return `Kaue das ${bolusName}!`;
  if (control.id === "galle_einlauf") return "Galle in den Zwölffingerdarm!";
  if (control.id === "bauchspeichel_einlauf") return "Bauchspeichel-Einlauf öffnen!";
  if (control.id === "ausscheiden") return "Stuhl ausscheiden!";
  return `${organ.name}: ${control.label}!`;
}

function randomItem<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomStep(min: number, max: number, step: number): number {
  const steps = Math.floor((max - min) / step);
  return min + Math.floor(Math.random() * (steps + 1)) * step;
}

function matches(v: ControlValue, expected: ControlValue): boolean {
  if (typeof expected === "boolean") return Boolean(v) === expected;
  return Number(v) === Number(expected);
}
